import {ElementRef, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Expand from '@arcgis/core/widgets/Expand';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import {WidgetUtilService} from '_@api/map-service/widget-util.service';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import Basemap from '@arcgis/core/Basemap';
import Point from "@arcgis/core/geometry/Point";
import {IQueryFeaturesOptions, queryFeatures} from "@esri/arcgis-rest-feature-service";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as route from "@arcgis/core/rest/route";
import Graphic from "@arcgis/core/Graphic";
import Color from "@arcgis/core/Color";
import RouteParameters from "@arcgis/core/rest/support/RouteParameters";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import {Constants} from "_@shared/models/constants";
import Collection from "@arcgis/core/core/Collection";
import Polygon from "@arcgis/core/geometry/Polygon";

export enum PositionEnum {
  TOP_RIGHT = 'top-right',
}

export interface InitialView {
  center: [number, number];
  zoom: number;
}

@Injectable({
  providedIn: 'root',
})
export class MapFactoryService {
  initialView: InitialView = {
    center: [-96.000, 38.500],
    zoom: 4,
  };
  
  private webMap: Map;
  private mapView: MapView;
  private renderer: Renderer2;
  private mapViewContainer: HTMLDivElement;
  
  constructor(
    private rendererFactory: RendererFactory2,
    private widgetUtilService: WidgetUtilService,
  ) {
  }
  
  public getMapView(): MapView {
    return this.mapView;
  }
  
  public initializeMapView(elementRef: ElementRef): MapView {
    console.log('Initialize map ....');
    this.createMapViewContainer(elementRef);
    this.createWebMap();
    this.createMapView();
    return this.mapView;
  }
  
  public removeMapViewContainer(elementRef: ElementRef): void {
    this.initializeRenderer();
    this.renderer.removeChild(elementRef.nativeElement, this.mapViewContainer);
  }
  
  /**
   * Load dataLayer by URL and options.
   * @param url - URL of service who return dataLayer
   * @param options - additional parameters for calling service
   *
   * @return @FeatureLayer object that can be process further.
   */
  public loadLayer(url: string, options?: FeatureLayer): FeatureLayer {
    const featureLayer = new FeatureLayer({
      url,
      objectIdField: 'ObjectID',
      outFields: ['*'],
    });
    
    if (options) {
      Object.keys(options).forEach(key => {
        switch (key) {
          case 'definitionExpression':
            featureLayer.definitionExpression = options.definitionExpression;
            break;
          case 'opacity':
            featureLayer.opacity = options.opacity;
            break;
          case 'id':
            featureLayer.id = options.id;
            break;
          case 'visible':
            featureLayer.visible = options.visible;
            break;
          case 'refreshInterval':
            featureLayer.refreshInterval = options.refreshInterval;
            break;
          case 'outFields':
            featureLayer.outFields = options.outFields;
            break;
          default:
            break;
        }
      });
    }
    return featureLayer;
  }
  
  /**
   * Add layer to basemap.
   * @param featureLayer
   * @param index
   */
  public addLayer(featureLayer: FeatureLayer | MapImageLayer | GraphicsLayer, index?: number) {
    if (index) {
      this.mapView.map.add(featureLayer, index);
    } else {
      this.mapView.map.add(featureLayer);
    }
  }
  
  private createMapViewContainer(elementRef: ElementRef): void {
    if (!elementRef) {
      return;
    }
    if (!this.mapViewContainer) {
      this.mapViewContainer = document.createElement('div');
      this.mapViewContainer.style.cssText = 'height: 100%';
    }
    this.initializeRenderer();
    this.renderer.appendChild(elementRef.nativeElement, this.mapViewContainer);
  }
  
  private initializeRenderer(): void {
    if (this.renderer == null) {
      this.renderer = this.rendererFactory.createRenderer(null, null);
    }
  }
  
  private createWebMap(): void {
    this.webMap = new Map({
      basemap: Basemap.fromId('gray-vector'),
    });
  }
  
  /**
   * Create MapView object.
   * @private
   */
  private createMapView(): void {
    if (this.mapView == null) {
      this.mapView = new MapView({
        container: this.mapViewContainer,
        map: this.webMap,
        center: this.initialView.center,
        zoom: this.initialView.zoom,
      });
      
      this.mapView.ui.move('zoom', 'top-right');
      this.addBasemapGallery(this.mapView);
    }
  }
  
  private addBasemapGallery(mapView: MapView) {
    const basemapGallery = new BasemapGallery({
      view: mapView,
      source: [Basemap.fromId('gray-vector'), Basemap.fromId('streets-vector'), Basemap.fromId('satellite')],
    });
    
    const expand = new Expand({
      view: mapView,
      content: basemapGallery,
      expandIcon: 'layer-basemap',
      autoCollapse: true,
      id: 'od-basemap',
    });
    
    this.addToMapView(expand, PositionEnum.TOP_RIGHT);
    this.widgetUtilService.setAutoCollapseEvent(expand, mapView);
  }
  
  public addCenterButton(mapView: MapView, defaultSVCMap?: Partial<InitialView>) {
    const button = document.createElement('span');
    button.className = 'esri-widget--button esri-widget esri-interactive';
    button.innerHTML = '<i class="esri-icon-locate"></i>';
    
    button.addEventListener('click', () => {
      this.getMapView().closePopup();
      const target = { ...this.initialView, ...defaultSVCMap };
      void mapView.goTo({
        center: target.center,
        zoom: target.zoom
      });
    });
    
    this.addToMapView(button, PositionEnum.TOP_RIGHT);
  }
  
  public addToMapView(element: any, position: PositionEnum) {
    this.getMapView().ui.add(element, {
      position,
    });
  }
  
  public removeFromMapView(element: any) {
    this.getMapView().ui.remove(element);
  }
  
  public removeLayerById(layerId: string) {
    const layer = this.getMapView().map.findLayerById(layerId);
    if (layer) {
      this.getMapView().map.remove(layer);
    }
  }
  
  queryLayerByUrl(queryOptions: IQueryFeaturesOptions, mapPoint?: Point): Promise<any> {
    if (mapPoint) {
      queryOptions.geometry = this.getPolygonByMapPoint(mapPoint)
    }
    return queryFeatures(queryOptions);
  }
  
  async queryLayerByLayer(layer: FeatureLayer, mapPoint: Point) {
    return layer.queryFeatures({
      geometry: mapPoint,
      outFields: ["*"],
      spatialRelationship: "intersects",
      returnGeometry: true
    });
  }
  
  public getBufferPolygonByMapPoint(mapPoint: Point) {
    const pixelWidth = this.getMapView().extent.width / this.getMapView().width;
    const toleranceInMapCoords = 10 * pixelWidth;
    return geometryEngine.buffer(mapPoint, toleranceInMapCoords, "meters") as any
  }
  
  public getPolygonByMapPoint(mapPoint: Point) {
    const pixelWidth = this.getMapView().extent.width / this.getMapView().width;
    const toleranceInMapCoords = 10 * pixelWidth;
    
    return {
      xmin: mapPoint.x - toleranceInMapCoords,
      ymin: mapPoint.y - toleranceInMapCoords,
      xmax: mapPoint.x + toleranceInMapCoords,
      ymax: mapPoint.y + toleranceInMapCoords,
      spatialReference: mapPoint.spatialReference
    } as unknown as Polygon;
  }
  
  public async solveRoute(url: string, graphics: Graphic[]) {
    let routeSolverLayer = new GraphicsLayer({id: Constants.ROUTING_SOLVER_LAYER_ID})
    
    const routeParams = new RouteParameters({
      stops: new FeatureSet({
        features: graphics
      }),
      returnRoutes: true,
      returnDirections: true,
      outSpatialReference: this.mapView.spatialReference
    });
    
    const result = await route.solve(url, routeParams);
    
    if (result.routeResults.length > 0) {
      const routeResult = result.routeResults[0].route;
      
      const routeGraphic = new Graphic({
        geometry: routeResult.geometry,
        symbol: {
          type: 'simple-line',
          width: 5,
          color: new Color([52, 229, 3, 0.7])
        }
      });
      
      routeSolverLayer.add(routeGraphic);
      this.addLayer(routeSolverLayer);
      await this.mapView.goTo(routeResult.geometry.extent);
    }
    
    return result;
  }
  
  public async expandByGraphics(graphics: Collection<Graphic>, factor = 1.5) {
    const geometries = graphics.map(g => g.geometry).toArray();
    const graphicUnion = geometryEngine.union(geometries);
    if (geometries && graphicUnion) {
      await this.getMapView().goTo(graphicUnion.extent.expand(factor));
    }
  }
  
}
